import {
    DETAILED_BOOKING_IMP_FIELDS, IMPFormErrorType
} from '../../constants/DetailedBookingConstants';
import { FORM_FIELDS } from '../../constants/FormFieldsConstants';
import {
    DetailedBookingIMPData, IMPContextualNodeData, PreviousNodesValues, SlotPackage,
} from '../../interface/DetailedBookingInterface';
import { calculateImpressionsAndShare } from './calculateImpressionsAndShare';
import { validateImpressionsAvailability } from './validateImpressionsAvailability';

export const canBypassBudgetLimit = (
    isApprovedBooking: boolean | undefined,
    existingValues: { impressions: number; valueToBeLockedAtPackageLevel: number } | undefined,
    totalValue: number,
    effectiveBudgetLimit: number,
): boolean => {
    return !!(isApprovedBooking && existingValues !== undefined
        && totalValue <= (effectiveBudgetLimit + existingValues.valueToBeLockedAtPackageLevel));
};

interface HandleContextualNodeChangeProps {
    nodeId: string,
    field: string,
    value: string,
    contextualNodes: IMPContextualNodeData[],
    setContextualNodes: React.Dispatch<React.SetStateAction<IMPContextualNodeData[]>>,
    formData: DetailedBookingIMPData,
    setErrorNodeId: React.Dispatch<React.SetStateAction<string>>,
    setErrorType: React.Dispatch<React.SetStateAction<IMPFormErrorType>>,
    setShowErrorModal: React.Dispatch<React.SetStateAction<boolean>>,
    selectedSlotPackage: string | null,
    slotPackagesData: SlotPackage[],
    isEditMode: boolean,
    setPreviousNodeValues: React.Dispatch<React.SetStateAction<PreviousNodesValues | null>>,
    isApprovedBooking?: boolean,
    existingContextualNodeValues?: Map<string, { impressions: number, valueToBeLockedAtPackageLevel: number }>,
    isWaitlistEnabled?: boolean,
}

export const handleContextualNodeChange = ({
    nodeId,
    field,
    value,
    contextualNodes,
    setContextualNodes,
    formData,
    setErrorNodeId,
    setErrorType,
    setShowErrorModal,
    selectedSlotPackage,
    slotPackagesData,
    isEditMode,
    setPreviousNodeValues,
    isApprovedBooking,
    existingContextualNodeValues,
    isWaitlistEnabled,
}: HandleContextualNodeChangeProps
) => {
    if (field === 'valueToBeLockedAtPackageLevel') {
        const inputValue = value === '' ? 0 : parseFloat(value) || 0;
        const effectiveBudgetLimit =
            parseFloat(formData.valueBlockedByLineItem.toString()) || 0;
        const currentNode = contextualNodes.find(n => n.id === nodeId);

        const contextualNodeKey = currentNode?.imSegment || '';
        const existingValues = isApprovedBooking && existingContextualNodeValues
            ? existingContextualNodeValues.get(contextualNodeKey)
            : undefined;

        if (!isEditMode && inputValue > 0 && inputValue > effectiveBudgetLimit) {
            if (!canBypassBudgetLimit(isApprovedBooking, existingValues, inputValue, effectiveBudgetLimit)) {
                setErrorNodeId(nodeId);
                setErrorType(IMPFormErrorType.BUDGET);
                setShowErrorModal(true);
                return;
            }
        }

        const sumOfOtherValues = contextualNodes
            .filter((otherNode) => otherNode.id !== nodeId)
            .reduce((total, otherNode) => {
                const otherValue =
                    parseFloat(otherNode.valueToBeLockedAtPackageLevel.toString()) || 0;
                return total + otherValue;
            }, 0);

        if (!isEditMode && inputValue > 0 && (inputValue + sumOfOtherValues) > effectiveBudgetLimit) {
            if (!canBypassBudgetLimit(isApprovedBooking, existingValues, inputValue + sumOfOtherValues, effectiveBudgetLimit)) {
                setErrorNodeId(nodeId);
                setErrorType(IMPFormErrorType.BUDGET);
                setShowErrorModal(true);
                return;
            }
        }

        const { impressions, share } = calculateImpressionsAndShare({
            nodeId,
            valueToLock: value,
            contextualNodes,
            selectedSlotPackage,
            slotPackagesData
        });

        if (impressions > 0) {
            if (!validateImpressionsAvailability({
                nodeId,
                calculatedImpressions: impressions,
                contextualNodes,
                selectedSlotPackage,
                slotPackagesData,
                isApprovedBooking,
                existingNodeImpressions: existingValues?.impressions,
                isWaitlistEnabled,
            })) {
                // Store previous values before showing error modal in edit mode
                if (isEditMode && currentNode) {
                    setPreviousNodeValues({
                        valueToBeLockedAtPackageLevel: currentNode.valueToBeLockedAtPackageLevel,
                        impressions: currentNode.impressions,
                        approxShareBlockedOverNode: currentNode.approxShareBlockedOverNode,
                    });
                }
                setErrorNodeId(nodeId);
                setErrorType(IMPFormErrorType.IMPRESSIONS);
                setShowErrorModal(true);
                return;
            }
        }

        setContextualNodes((prev) =>
            prev.map((node) =>
                node.id === nodeId
                    ? {
                        ...node,
                        valueToBeLockedAtPackageLevel: value === '' ? 0 : parseFloat(value) || 0,
                        impressions,
                        approxShareBlockedOverNode: share || 0,
                    }
                    : node
            )
        );
    } else {
        // Handle numeric processing from current version for other fields
        const numericFields: string[] = [
            FORM_FIELDS.VALUE_BLOCKED_BY_LINE_ITEM,
            FORM_FIELDS.IMPRESSIONS,
            FORM_FIELDS.APPROX_SHARE_BLOCKED_OVER_NODE,
        ];
        const processedValue = numericFields.includes(field)
            ? value === ''
                ? 0
                : parseFloat(value) || 0
            : value;

        setContextualNodes((prev) =>
            prev.map((node) => {
                if (node.id === nodeId) {
                    if (field === DETAILED_BOOKING_IMP_FIELDS.IM_SEGMENT) {
                        if (node.imSegment !== processedValue as string) return {
                            ...node,
                            imSegment: processedValue as string,
                            valueToBeLockedAtPackageLevel: 0,
                            impressions: 0,
                            approxShareBlockedOverNode: 0,
                        };
                        return { ...node, imSegment: processedValue as string };
                    } else if (field === 'valueToBeLockedAtPackageLevel') {
                        return {
                            ...node,
                            valueToBeLockedAtPackageLevel: processedValue as number,
                        };
                    } else if (field === 'impressions') {
                        return { ...node, impressions: processedValue as number };
                    } else if (field === 'approxShareBlockedOverNode') {
                        return {
                            ...node,
                            approxShareBlockedOverNode: processedValue as number,
                        };
                    } else {
                        return { ...node, [field]: processedValue };
                    }
                }
                return node;
            })
        );
    }
};
